import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Linkedin, Apple, Play } from 'lucide-react';
import logo from '@/assets/logos/coffeeback-logo-gpt.png';

const fadeInUp = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } },
};

export function LandingFooter() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const productLinks = [
        { label: t('footer.links.home'), id: 'home' },
        { label: t('footer.links.howItWorks'), id: 'how-it-works' },
        { label: t('footer.links.features'), id: 'consumer-section' },
        { label: t('footer.links.forBusiness'), id: 'business-section' },
        { label: t('footer.links.pricing'), id: 'pricing-section' },
        { label: t('footer.links.faq'), id: 'faq-section' },
        { label: t('footer.links.contact'), id: 'contact-section' },
    ];

    const handleProductLinkClick = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        const scrollBehavior = () => {
            if (id === 'home') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }
        };

        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(scrollBehavior, 150);
        } else {
            setTimeout(scrollBehavior, 150);
        }
    };

    const policiesLinks = [
        { label: t('footer.links.privacy'), href: '/legal/privacy' },
        { label: t('footer.links.cookies'), href: '/legal/cookies' },
    ];

    const legalLinks = [
        { label: t('footer.links.terms'), href: '/legal/terms' },
        { label: t('footer.links.lgpd'), href: '/legal/lgpd' },

        { label: t('footer.links.businessTerms'), href: '/legal/business-terms' },
        { label: t('footer.links.dataRights'), href: '/legal/data-rights' },
        { label: t('footer.links.community'), href: '/legal/community' },
    ];

    return (
        <footer className="relative overflow-hidden">

            {/* ─── Block 1: Subtle CTA Strip ─── */}
            {/* <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeInUp}
                className="relative py-6 md:py-8"
                style={{
                    background: 'linear-gradient(180deg, #3D3028 0%, #2D1810 100%)',
                }}
            >
                
                <div
                    className="absolute inset-x-0 -top-px h-px"
                    style={{
                        background: 'linear-gradient(90deg, transparent 0%, #6F4E37 50%, transparent 100%)',
                    }}
                />
                <div className="container h-full mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-base md:text-lg text-[#C8B9A9] text-center md:text-left">
                        {t('footer.cta.headline')}
                    </p>
                    <div className="flex items-center gap-3">
                        <a
                            href="#"
                            className="inline-flex items-center gap-2 text-sm font-medium text-[#C8A27A] hover:text-[#D4B08A] transition-colors duration-300"
                        >
                            {t('footer.cta.downloadApp')}
                            <ArrowRight size={14} />
                        </a>
                        <span className="text-[#C8B9A9]/30">|</span>
                        <a
                            href="#business-section"
                            className="inline-flex items-center gap-2 text-sm font-medium text-[#C8B9A9]/70 hover:text-[#C8A27A] transition-colors duration-300"
                        >
                            {t('footer.cta.forBusiness')}
                            <ArrowRight size={14} />
                        </a>
                    </div>
                </div>
            </motion.div> */}

            {/* ─── Main Footer ─── */}
            <div
                className="relative"
                style={{
                    background: 'linear-gradient(180deg, #2D1810 0%, #241912 60%, #1F1712 100%)',
                }}
            >
                {/* Grain Texture Overlay */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-[0.02]"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: '200px',
                    }}
                />

                {/* ─── Unified Footer Content (5-col desktop, stacked mobile) ─── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={stagger}
                    className="container mx-auto px-4 relative z-10 py-12 md:py-14"
                >
                    <div className="grid grid-cols-2 md:grid-cols-[1.3fr_1fr_0.8fr_0.8fr_auto] gap-8 lg:gap-10">

                        {/* Brand Column — full-width centered on mobile, left-aligned on desktop */}
                        <motion.div
                            variants={fadeInUp}
                            className="col-span-2 md:col-span-1 text-center md:text-left pb-6 md:pb-0"
                        >
                            {/* Logo + Name */}
                            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                                <img src={logo} alt="Coffeeback" className="h-14 sm:h-16 md:h-10 w-auto brightness-0 invert opacity-90" />
                                <span className="font-display font-bold text-2xl sm:text-3xl md:text-xl italic text-[#F5EDE4]">
                                    Coffeeback
                                </span>
                            </div>
                            {/* Tagline */}
                            <p
                                className="text-sm text-[#C8B9A9] leading-relaxed mx-auto md:mx-0 mb-4"
                                style={{ maxWidth: '240px', whiteSpace: 'normal', wordBreak: 'normal' }}
                            >
                                {t('footer.tagline')}
                            </p>
                            {/* Social icons */}
                            <div className="flex items-center justify-center md:justify-start gap-3">
                                {[
                                    { icon: Instagram, label: 'Instagram', href: '#' },
                                    { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/taoker-tecnologia' },
                                ].map(({ icon: Icon, label, href }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target={href !== '#' ? '_blank' : '_self'}
                                        rel={href !== '#' ? 'noopener noreferrer' : ''}
                                        aria-label={label}
                                        className="w-8 h-8 rounded-full border border-[#C8B9A9]/15 flex items-center justify-center text-[#C8B9A9]/70 hover:text-[#C8A27A] hover:border-[#C8A27A]/30 transition-all duration-300"
                                    >
                                        <Icon size={14} />
                                    </a>
                                ))}
                            </div>
                        </motion.div>

                        {/* Mobile-only separator — spans full width, hidden on desktop */}
                        <div className="col-span-2 md:hidden">
                            <div
                                className="h-px"
                                style={{
                                    background: 'linear-gradient(90deg, transparent 0%, rgba(111,78,55,0.3) 30%, rgba(166,124,82,0.2) 50%, rgba(111,78,55,0.3) 70%, transparent 100%)',
                                }}
                            />
                        </div>

                        {/* Col 2: Product Links */}
                        <motion.div variants={fadeInUp} className="order-1 md:order-none">
                            <h4 className="font-display font-semibold text-[#F5EDE4]/90 mb-3 text-sm uppercase tracking-wider">
                                {t('footer.columns.product')}
                            </h4>
                            <ul className="space-y-2">
                                {productLinks.map(({ label, id }) => (
                                    <li key={label}>
                                        <button
                                            onClick={(e) => handleProductLinkClick(e, id)}
                                            className="text-sm text-[#C8B9A9]/70 hover:text-[#C8A27A] transition-colors duration-300 bg-transparent border-none p-0 cursor-pointer text-left"
                                        >
                                            {label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Col 3: policies Links */}
                        <motion.div variants={fadeInUp} className="order-3 md:order-none">
                            <h4 className="font-display font-semibold text-[#F5EDE4]/90 mb-3 text-sm uppercase tracking-wider">
                                {t('footer.columns.policies')}
                            </h4>
                            <ul className="space-y-2">
                                {policiesLinks.map(({ label, href }) => (
                                    <li key={label}>
                                        <a
                                            href={href}
                                            className="text-sm text-[#C8B9A9]/70 hover:text-[#C8A27A] transition-colors duration-300"
                                        >
                                            {label}
                                        </a>
                                    </li>
                                ))}
                                <li>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.dispatchEvent(new Event('open-cookie-banner'));
                                        }}
                                        className="text-sm text-[#C8B9A9]/70 hover:text-[#C8A27A] transition-colors duration-300 bg-transparent border-none p-0 cursor-pointer text-left"
                                    >
                                        {t('legal.cookieBanner.customize', 'Preferências de Cookies')}
                                    </button>
                                </li>
                            </ul>
                        </motion.div>

                        {/* Col 4: Legal Links */}
                        <motion.div variants={fadeInUp} className="order-2 md:order-none">
                            <h4 className="font-display font-semibold text-[#F5EDE4]/90 mb-3 text-sm uppercase tracking-wider">
                                {t('footer.columns.legal')}
                            </h4>
                            <ul className="space-y-2">
                                {legalLinks.map(({ label, href }) => (
                                    <li key={label}>
                                        <Link
                                            to={href}
                                            className="text-sm text-[#C8B9A9]/70 hover:text-[#C8A27A] transition-colors duration-300"
                                        >
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Col 5: Store Badges */}
                        <motion.div variants={fadeInUp} className="order-4 md:order-none">
                            <div className="flex flex-col sm:flex-row md:flex-col gap-2.5">
                                <a
                                    href="#"
                                    className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#C8B9A9]/15 hover:border-[#C8A27A]/30 transition-all duration-300 group"
                                >
                                    <Apple size={16} className="text-[#C8B9A9]/60 group-hover:text-[#C8A27A] transition-colors shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-[9px] text-[#C8B9A9]/50 leading-none">
                                            {t('footer.download.subtitle').split(' ')[0]}
                                        </p>
                                        <p className="text-xs font-semibold text-[#F5EDE4]/90 leading-tight whitespace-nowrap">
                                            {t('footer.download.appStore')}
                                        </p>
                                    </div>
                                </a>
                                <a
                                    href="#"
                                    className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#C8B9A9]/15 hover:border-[#C8A27A]/30 transition-all duration-300 group"
                                >
                                    <Play size={16} className="text-[#C8B9A9]/60 group-hover:text-[#C8A27A] transition-colors shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-[9px] text-[#C8B9A9]/50 leading-none">
                                            {t('footer.download.subtitle').split(' ')[0]}
                                        </p>
                                        <p className="text-xs font-semibold text-[#F5EDE4]/90 leading-tight whitespace-nowrap">
                                            {t('footer.download.googlePlay')}
                                        </p>
                                    </div>
                                </a>
                            </div>
                        </motion.div>

                    </div>
                </motion.div>

                {/* ─── Copyright (seamless, no separate block) ─── */}
                <div className="container mx-auto px-4 relative z-10">
                    <div
                        className="h-px"
                        style={{
                            background: 'linear-gradient(90deg, transparent 0%, #6F4E37 30%, #A67C52 50%, #6F4E37 70%, transparent 100%)',
                        }}
                    />
                    <div className="flex flex-col md:flex-row items-center justify-center gap-2 py-5 text-xs text-[#8C7E70]/70">
                        <p>{t('footer.copyright')}</p>
                        {/* <p>{t('footer.madeBy')}</p> */}
                    </div>
                </div>
            </div>
        </footer>
    );
}
